// src/components/JobSearchFilters.tsx

import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { ApplicationStatus } from '../types/jobSearch';
import { statusConfig } from '../utils/jobApplicationUtils';
import { IoFunnelOutline, IoCalendarOutline, IoAlbumsOutline, IoChevronDownOutline, IoChevronUpOutline } from 'react-icons/io5';

interface JobSearchFiltersProps {
    availableTags: string[];
    selectedTags: string[];
    selectedStatuses: ApplicationStatus[];
    dateRange: { start: Date | null; end: Date | null };
    onTagsChange: (tags: string[]) => void;
    onStatusesChange: (statuses: ApplicationStatus[]) => void;
    onDateRangeChange: (range: { start: Date | null; end: Date | null }) => void;
}

const JobSearchFilters: React.FC<JobSearchFiltersProps> = ({
    availableTags,
    selectedTags,
    selectedStatuses,
    dateRange,
    onTagsChange,
    onStatusesChange,
    onDateRangeChange
}) => {
    const { darkMode } = useDarkMode();
    
    const [isExpanded, setIsExpanded] = useState(() => {
        const saved = localStorage.getItem('jobSearchFilters.isExpanded');
        return saved ? JSON.parse(saved) : false; // Default to collapsed
    });

    useEffect(() => {
        localStorage.setItem('jobSearchFilters.isExpanded', JSON.stringify(isExpanded));
    }, [isExpanded]);

    const hasActiveFilters = selectedTags.length > 0 || 
        selectedStatuses.length > 0 || 
        dateRange.start !== null || 
        dateRange.end !== null;

    const clearFilters = () => {
        onTagsChange([]);
        onStatusesChange([]);
        onDateRangeChange({ start: null, end: null });
    };

    return (
        <div className={`rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
            <div 
                className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center space-x-2">
                    <IoFunnelOutline size={20} />
                    <h3 className="text-lg font-semibold">Filters</h3>
                    {hasActiveFilters && (
                        <span className="text-sm text-blue-500">
                            ({selectedTags.length + selectedStatuses.length + (dateRange.start || dateRange.end ? 1 : 0)} active)
                        </span>
                    )}
                </div>
                <div className="flex items-center space-x-2">
                    {hasActiveFilters && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation(); // Prevent expansion toggle
                                clearFilters();
                            }}
                            className={`text-sm px-3 py-1 rounded transition-colors
                                ${hasActiveFilters 
                                    ? 'bg-blue-500 text-white hover:bg-blue-600' 
                                    : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            Clear Filters
                        </button>
                    )}
                    <button className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600">
                        {isExpanded ? <IoChevronUpOutline size={20} /> : <IoChevronDownOutline size={20} />}
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="p-4 border-t space-y-4">
                    {/* Status Filters */}
                    <div>
                        <h4 className="text-sm font-medium mb-2">Status</h4>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(statusConfig).map(([status, config]) => (
                                <button
                                    key={status}
                                    onClick={() => {
                                        const newStatuses = selectedStatuses.includes(status as ApplicationStatus)
                                            ? selectedStatuses.filter(s => s !== status)
                                            : [...selectedStatuses, status as ApplicationStatus];
                                        onStatusesChange(newStatuses);
                                    }}
                                    className={`px-3 py-1 rounded-full text-sm flex items-center space-x-1
                                        ${selectedStatuses.includes(status as ApplicationStatus)
                                            ? config.color + ' text-white'
                                            : darkMode ? 'bg-gray-700' : 'bg-gray-200'
                                        }`}
                                >
                                    <span>{config.icon}</span>
                                    <span>{config.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tag Filters */}
                    <div>
                        <h4 className="text-sm font-medium mb-2">Tags</h4>
                        <div className="flex flex-wrap gap-2">
                            {availableTags.map(tag => (
                                <button
                                    key={tag}
                                    onClick={() => {
                                        const newTags = selectedTags.includes(tag)
                                            ? selectedTags.filter(t => t !== tag)
                                            : [...selectedTags, tag];
                                        onTagsChange(newTags);
                                    }}
                                    className={`px-3 py-1 rounded-full text-sm flex items-center
                    ${selectedTags.includes(tag)
                                            ? 'bg-blue-500 text-white'
                                            : darkMode ? 'bg-gray-700' : 'bg-gray-200'
                                        }`}
                                >
                                    <IoAlbumsOutline className="mr-1" />
                                    {tag}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Date Range Filter */}
                    <div>
                        <h4 className="text-sm font-medium mb-2">Date Range</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm mb-1" htmlFor="start-date">Start Date</label>
                                <input
                                    id="start-date"
                                    type="date"
                                    value={dateRange.start?.toISOString().split('T')[0] || ''}
                                    onChange={(e) => {
                                        const date = e.target.value ? new Date(e.target.value) : null;
                                        // Set time to start of day
                                        if (date) {
                                            date.setHours(0, 0, 0, 0);
                                        }
                                        onDateRangeChange({
                                            ...dateRange,
                                            start: date
                                        });
                                    }}
                                    className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                                />
                            </div>
                            <div>
                                <label className="block text-sm mb-1" htmlFor="end-date">End Date</label>
                                <input
                                    id="end-date"
                                    type="date"
                                    value={dateRange.end?.toISOString().split('T')[0] || ''}
                                    onChange={(e) => {
                                        const date = e.target.value ? new Date(e.target.value) : null;
                                        // Set time to end of day
                                        if (date) {
                                            date.setHours(23, 59, 59, 999);
                                        }
                                        onDateRangeChange({
                                            ...dateRange,
                                            end: date
                                        });
                                    }}
                                    className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobSearchFilters;